// =============================================================================
// Aarthika ERP — License System (TypeScript / Frontend)
// =============================================================================
// Changes from original:
//  + importLicenseFile()      — .aarthika file parse + Tauri RSA verify
//  + activateLicenseFile()    — First-time online activation with hardware bind
//  + checkLicenseOnBoot()     — Boot check via Tauri (hardware + grace period)
//  + revalidateOnline()       — Grace period reset when internet available
// All existing functions (activateLicense, generateTransferCode, etc.) retained.
// =============================================================================

import {
  doc, getDoc, setDoc, updateDoc, runTransaction, onSnapshot, collection, getDocs
} from 'firebase/firestore';
import { db } from './firebase';
// Tauri invoke — lazy loaded only when running inside Tauri desktop app
async function tauriInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  try {
    // Dynamic import — browser mein yeh gracefully fail hoga
    const tauri = await import('@tauri-apps/api/tauri').catch(() => null);
    if (!tauri) throw new Error('Not running in Tauri');
    return tauri.invoke<T>(cmd, args);
  } catch {
    throw new Error(`Tauri command '${cmd}' not available in browser environment`);
  }
}

export type PlanType = 'free' | 'basic' | 'pro' | 'unlimited';

// ─────────────────────────────────────────────────────────────────────────────
// TRIAL / PLAN LIMITS
// ─────────────────────────────────────────────────────────────────────────────
export interface PlanLimits {
  maxCompanies: number;
  maxVouchers: number;
  maxLedgers: number;
}

export const PLAN_LIMITS: Record<PlanType | 'demo', PlanLimits> = {
  free:      { maxCompanies: 1,   maxVouchers: 30,     maxLedgers: 10    },
  demo:      { maxCompanies: 1,   maxVouchers: 50,     maxLedgers: 20    },
  basic:     { maxCompanies: 3,   maxVouchers: 5000,   maxLedgers: 500   },
  pro:       { maxCompanies: 10,  maxVouchers: 99999,  maxLedgers: 9999  },
  unlimited: { maxCompanies: 999, maxVouchers: 999999, maxLedgers: 999999},
};

export const getCurrentPlanLimits = (): PlanLimits => {
  const info = getStoredLicense();
  if (!info || !isLicenseValid(info)) return PLAN_LIMITS['demo'];
  const key = info.type === 'demo' ? 'demo' : (info.plan ?? 'demo');
  return PLAN_LIMITS[key as keyof typeof PLAN_LIMITS] ?? PLAN_LIMITS['demo'];
};

// ─────────────────────────────────────────────────────────────────────────────
// .aarthika FILE TYPES
// ─────────────────────────────────────────────────────────────────────────────

export interface LicensePayload {
  licenseKey: string;
  customer: string;
  plan: PlanType;
  maxCompanies: number;
  maxDevices: number;
  validityDays: number;
  expiresAt: string | null;
  issuedAt: string;
  hardwareFingerprint: string | null; // null at issue, filled on activation
}

export interface AarthikaLicenseFile {
  payload: LicensePayload;
  signature: string; // Base64 RSA-2048 signature
}

// ─────────────────────────────────────────────────────────────────────────────
// EXISTING LICENSE INFO (local cache)
// ─────────────────────────────────────────────────────────────────────────────

export interface LicenseInfo {
  type: 'demo' | 'full';
  plan: PlanType;
  activatedAt: string;
  expiresAt: string | null;
  key: string | null;
  customer?: string;
  deviceId: string;
  maxCompanies?: number;
  // NEW: .aarthika file based activation
  activationMethod?: 'key' | 'file';
  hardwareFingerprint?: string;
  offlineGraceExpiry?: string;
}

export interface ValidationResponse {
  valid: boolean;
  plan: PlanType;
  expiresAt?: string | null;
  customer?: string;
  maxCompanies?: number;
  reason?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// INSTALL ID (existing — unchanged)
// ─────────────────────────────────────────────────────────────────────────────

const IDB_NAME  = 'erp_license_store';
const IDB_VER   = 1;
const IDB_STORE = 'license';

function openIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VER);
    req.onupgradeneeded = () => { req.result.createObjectStore(IDB_STORE); };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function idbGet(key: string): Promise<any> {
  try {
    const idb = await openIDB();
    return new Promise((res, rej) => {
      const req = idb.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(key);
      req.onsuccess = () => res(req.result ?? null);
      req.onerror   = () => rej(req.error);
    });
  } catch { return null; }
}

async function idbSet(key: string, value: any): Promise<void> {
  try {
    const idb = await openIDB();
    await new Promise<void>((res, rej) => {
      const req = idb.transaction(IDB_STORE, 'readwrite').objectStore(IDB_STORE).put(value, key);
      req.onsuccess = () => res();
      req.onerror   = () => rej(req.error);
    });
  } catch { /* ignore */ }
}

async function idbDelete(key: string): Promise<void> {
  try {
    const idb = await openIDB();
    await new Promise<void>((res) => {
      const tx = idb.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(key);
      tx.oncomplete = () => res();
    });
  } catch { /* ignore */ }
}

export const getInstallId = async (): Promise<string> => {
  let id = await idbGet('installId');
  if (id) return id;
  id = localStorage.getItem('erp_device_id') || localStorage.getItem('erp_install_id');
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  await idbSet('installId', id);
  localStorage.setItem('erp_install_id', id);
  return id;
};

export const getDeviceId = (): string => {
  return localStorage.getItem('erp_install_id') || localStorage.getItem('erp_device_id') || 'unknown';
};

// ─────────────────────────────────────────────────────────────────────────────
// LOCAL CACHE (existing — unchanged)
// ─────────────────────────────────────────────────────────────────────────────

export const getStoredLicense = (): LicenseInfo | null => {
  const data = localStorage.getItem('erp_license');
  if (!data) return null;
  try { return JSON.parse(data) as LicenseInfo; } catch { return null; }
};

export const getStoredLicenseAsync = async (): Promise<LicenseInfo | null> => {
  const cached = await idbGet('license');
  if (cached) {
    localStorage.setItem('erp_license', JSON.stringify(cached));
    return cached as LicenseInfo;
  }
  return getStoredLicense();
};

export const saveStoredLicense = (info: LicenseInfo): void => {
  localStorage.setItem('erp_license', JSON.stringify(info));
  idbSet('license', info).catch(() => {});
};

export const clearStoredLicense = (): void => {
  localStorage.removeItem('erp_license');
  localStorage.removeItem('erp_last_checked');
  idbDelete('license').catch(() => {});
};

// ─────────────────────────────────────────────────────────────────────────────
// NETWORK CHECK (existing — unchanged)
// ─────────────────────────────────────────────────────────────────────────────

export const checkOnline = async (): Promise<boolean> => {
  if (!navigator.onLine) return false;
  const targets = [
    'https://firestore.googleapis.com/',
    'https://www.google.com/favicon.ico',
    'https://fonts.googleapis.com/',
  ];
  for (const url of targets) {
    try {
      const res = await fetch(url, { method: 'HEAD', mode: 'no-cors', cache: 'no-store', signal: AbortSignal.timeout(4000) });
      if (res.type === 'opaque' || res.ok) return true;
    } catch { /* try next */ }
  }
  return navigator.onLine;
};

// ─────────────────────────────────────────────────────────────────────────────
// LOCAL VALIDITY CHECK (existing — unchanged)
// ─────────────────────────────────────────────────────────────────────────────

export const isLicenseValid = (info: LicenseInfo | null): boolean => {
  if (!info) return false;
  const now = Date.now();
  const last = parseInt(localStorage.getItem('erp_last_checked') || '0', 10);
  if (last && now < last) return false;
  localStorage.setItem('erp_last_checked', now.toString());
  if (info.type === 'demo') {
    return now - new Date(info.activatedAt).getTime() < 7 * 24 * 60 * 60 * 1000;
  }
  if (info.expiresAt && now > new Date(info.expiresAt).getTime()) return false;
  return true;
};

// ─────────────────────────────────────────────────────────────────────────────
// .aarthika FILE — IMPORT & VERIFICATION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * .aarthika file content string se parse + RSA verify karo (Tauri Rust command)
 * Offline bhi kaam karta hai — RSA public key EXE mein baked-in hai
 */
export const verifyLicenseFile = async (fileContent: string): Promise<{
  valid: boolean;
  reason?: string;
  payload?: LicensePayload;
}> => {
  try {
    const result = await tauriInvoke<{
      valid: boolean;
      reason?: string;
      licenseKey?: string;
      customer?: string;
      plan?: string;
      maxCompanies?: number;
      expiresAt?: string;
      hardwareBound: boolean;
    }>('verify_license_file', { fileContent });

    if (!result.valid) {
      return { valid: false, reason: result.reason || 'License file invalid' };
    }

    // Parse payload for JS use
    const parsed: AarthikaLicenseFile = JSON.parse(fileContent);
    return { valid: true, payload: parsed.payload };

  } catch (e: any) {
    return { valid: false, reason: e?.message || 'License file verification failed' };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// FIRST-TIME ONLINE ACTIVATION (File Based)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * .aarthika file se first-time activation:
 *  1. RSA verify (Rust)
 *  2. Firestore check — key exists? not revoked? not expired?
 *  3. Hardware fingerprint generate (Rust)
 *  4. Firestore update — hardwareFingerprint, status: 'active'
 *  5. Rust cache save (encrypted, hardware bound)
 */
export const activateLicenseFile = async (
  fileContent: string
): Promise<{ success: boolean; reason?: string }> => {

  // Step 1: RSA verify
  const verifyResult = await verifyLicenseFile(fileContent);
  if (!verifyResult.valid || !verifyResult.payload) {
    return { success: false, reason: verifyResult.reason };
  }

  const payload = verifyResult.payload;
  const licenseKey = payload.licenseKey;

  // Step 2: Internet mandatory for first activation
  const online = await checkOnline();
  if (!online) {
    return { success: false, reason: 'First-time activation requires internet connection. Please connect and try again.' };
  }

  // Step 3: Firestore check
  const licRef = doc(db, 'licenses', licenseKey);
  const installId = await getInstallId();

  try {
    // Step 4: Firestore transaction — atomic hardware bind
    const hardwareFingerprint: string = await tauriInvoke('get_hardware_fingerprint_cmd');

    const txResult = await runTransaction(db, async (tx) => {
      const snap = await tx.get(licRef);
      if (!snap.exists()) throw new Error('License key not found in server. Contact support.');

      const d = snap.data();
      if (d.status === 'revoked') throw new Error('This license has been revoked. Contact support.');

      if (d.expiresAt && new Date(d.expiresAt).getTime() < Date.now()) {
        throw new Error('This license has expired. Please renew.');
      }

      // Hardware fingerprint check — agar already bound hai kisi aur machine pe
      if (d.hardwareFingerprint && d.hardwareFingerprint !== hardwareFingerprint) {
        throw new Error('This license is already activated on a different machine. Contact support to transfer.');
      }

      // Device count check
      const maxDevicesAllowed = d.maxDevices || 1;
      const devices = Array.isArray(d.devices) ? d.devices : [];
      const alreadyThisDevice = devices.includes(installId);

      if (!alreadyThisDevice && devices.length >= maxDevicesAllowed) {
        throw new Error(`License already activated on ${maxDevicesAllowed} device(s). Contact support to reset.`);
      }

      const updatedDevices = alreadyThisDevice ? devices : [...devices, installId];
      const now = new Date().toISOString();
      const offlineGraceExpiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      tx.update(licRef, {
        status: 'active',
        activatedAt: d.activatedAt || now,
        devices: updatedDevices,
        activationCount: updatedDevices.length,
        hardwareFingerprint,
        activationMethod: 'file',
        licenseFileIssuedAt: payload.issuedAt,
        lastOnlineCheck: now,
        offlineGraceExpiry,
      });

      return {
        plan: d.plan as PlanType,
        customer: d.customer,
        maxCompanies: d.maxCompanies || payload.maxCompanies,
        expiresAt: d.expiresAt || payload.expiresAt || null,
        offlineGraceExpiry,
      };
    });

    // Step 5: Rust cache save (hardware-bound, 30 day grace)
    await tauriInvoke('save_activation_cache', {
      fileContent,
      firestoreOfflineGraceExpiry: txResult.offlineGraceExpiry,
    });

    // Step 6: Save local JS license info
    const newLicense: LicenseInfo = {
      type: 'full',
      plan: txResult.plan,
      activatedAt: new Date().toISOString(),
      expiresAt: txResult.expiresAt,
      key: licenseKey,
      customer: txResult.customer,
      deviceId: installId,
      maxCompanies: txResult.maxCompanies,
      activationMethod: 'file',
      hardwareFingerprint,
      offlineGraceExpiry: txResult.offlineGraceExpiry,
    };
    saveStoredLicense(newLicense);

    return { success: true };

  } catch (e: any) {
    return { success: false, reason: e?.message || 'Activation failed. Please try again.' };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// BOOT LICENSE CHECK (Tauri Hardware Verify)
// ─────────────────────────────────────────────────────────────────────────────

export interface TauriLicenseStatus {
  valid: boolean;
  reason?: string;
  licenseKey?: string;
  customer?: string;
  plan?: string;
  maxCompanies?: number;
  expiresAt?: string;
  hardwareBound: boolean;
  graceExpiresAt?: string;
}

/**
 * App boot pe call karo — Tauri Rust se hardware + grace period check
 * Agar grace expired: online revalidation ki zaroorat
 */
export const checkLicenseOnBoot = async (): Promise<TauriLicenseStatus> => {
  try {
    const status = await tauriInvoke<TauriLicenseStatus>('check_license_valid');
    return status;
  } catch (e: any) {
    // Tauri invoke fail = Tauri unavailable (dev mode browser), fall back to JS check
    const local = getStoredLicense();
    if (local && isLicenseValid(local)) {
      return {
        valid: true,
        hardwareBound: false,
        licenseKey: local.key || undefined,
        customer: local.customer,
        plan: local.plan,
        maxCompanies: local.maxCompanies,
        expiresAt: local.expiresAt || undefined,
      };
    }
    return { valid: false, reason: 'No valid license found', hardwareBound: false };
  }
};

/**
 * Online pe revalidate karo + grace period 30 din extend karo
 * Background mein call karo jab bhi internet available ho
 */
export const revalidateOnline = async (): Promise<void> => {
  const local = getStoredLicense();
  if (!local?.key || local.activationMethod !== 'file') return;

  const online = await checkOnline();
  if (!online) return;

  try {
    // Firestore check
    const snap = await getDoc(doc(db, 'licenses', local.key));
    if (!snap.exists()) return;

    const d = snap.data();
    if (d.status === 'revoked') {
      clearStoredLicense();
      await tauriInvoke('clear_license_cache');
      return;
    }

    // Grace extend karo
    const newGrace = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
    await updateDoc(doc(db, 'licenses', local.key), {
      lastOnlineCheck: new Date().toISOString(),
      offlineGraceExpiry: newGrace,
    });

    // Rust cache mein bhi extend karo
    await tauriInvoke('extend_grace_period');

    // JS cache update
    saveStoredLicense({ ...local, offlineGraceExpiry: newGrace });

  } catch { /* offline — ignore */ }
};

// ─────────────────────────────────────────────────────────────────────────────
// EXISTING FUNCTIONS — UNCHANGED (Key-based activation, still works)
// ─────────────────────────────────────────────────────────────────────────────

const installRef = async () => {
  const id = await getInstallId();
  return doc(db, 'installations', id);
};

async function saveInstallation(info: LicenseInfo): Promise<void> {
  try {
    const ref = await installRef();
    await setDoc(ref, {
      licenseKey:  info.key,
      plan:        info.plan,
      type:        info.type,
      activatedAt: info.activatedAt,
      expiresAt:   info.expiresAt ?? null,
      customer:    info.customer ?? null,
      updatedAt:   new Date().toISOString(),
    }, { merge: true });
  } catch { /* offline */ }
}

async function loadInstallation(): Promise<LicenseInfo | null> {
  try {
    const ref  = await installRef();
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const d = snap.data();
    return {
      type:        d.type        || 'full',
      plan:        d.plan        || 'basic',
      activatedAt: d.activatedAt || new Date().toISOString(),
      expiresAt:   d.expiresAt   || null,
      key:         d.licenseKey  || null,
      customer:    d.customer    || undefined,
      deviceId:    await getInstallId(),
    };
  } catch { return null; }
}

export const resolveLicenseOnBoot = async (): Promise<LicenseInfo | null> => {
  const local = await getStoredLicenseAsync();
  const online = await checkOnline();
  if (online) {
    const remote = await loadInstallation();
    if (remote && isLicenseValid(remote)) {
      saveStoredLicense(remote);
      return remote;
    }
  }
  return local;
};

export const checkKeyFormat = async (key: string): Promise<ValidationResponse> => {
  const trimmed        = key.trim().toUpperCase();
  const isNewFormat    = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(trimmed);
  const isLegacyFormat = /^AAR-[A-Z]+-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(trimmed);

  if (!isNewFormat && !isLegacyFormat) {
    return { valid: false, plan: 'free', reason: 'Invalid license key format.' };
  }

  try {
    const snap = await getDoc(doc(db, 'licenses', trimmed));
    if (!snap.exists()) return { valid: false, plan: 'free', reason: 'License key not found.' };
    const d = snap.data();
    if (d.status === 'revoked') return { valid: false, plan: 'free', reason: 'This license has been revoked.' };
    if (d.expiresAt && new Date(d.expiresAt).getTime() < Date.now())
                        return { valid: false, plan: 'free', reason: 'This license has expired.' };
    const maxDevicesAllowed = d.maxDevices || 1;
    const devices           = Array.isArray(d.devices) ? d.devices : [];
    const installId          = await getInstallId();
    const thisDeviceAlready  = devices.includes(installId);
    if (!thisDeviceAlready && devices.length >= maxDevicesAllowed) {
      return { valid: false, plan: 'free', reason: `License already activated on ${maxDevicesAllowed} device(s). Contact support to reset.` };
    }
    return { valid: true, plan: d.plan as PlanType, expiresAt: d.expiresAt || null, customer: d.customer, maxCompanies: d.maxCompanies || 1 };
  } catch {
    return { valid: false, plan: 'free', reason: 'Network error. Please check your connection.' };
  }
};

export const activateLicense = async (key: string): Promise<{ success: boolean; reason?: string }> => {
  const trimmed   = key.trim().toUpperCase();
  const installId = await getInstallId();
  const licRef    = doc(db, 'licenses', trimmed);
  let txResult: ValidationResponse | null = null;

  try {
    txResult = await runTransaction(db, async (tx) => {
      const snap = await tx.get(licRef);
      if (!snap.exists()) throw new Error('License key not found.');
      const d = snap.data();
      if (d.status === 'revoked') throw new Error('This license has been revoked.');
      if (d.expiresAt && new Date(d.expiresAt).getTime() < Date.now()) throw new Error('This license has expired.');
      const maxDevicesAllowed = d.maxDevices || 1;
      const devices           = Array.isArray(d.devices) ? d.devices : [];
      const alreadyOnThisDevice = devices.includes(installId);
      if (!alreadyOnThisDevice && devices.length >= maxDevicesAllowed) {
        throw new Error(`License already activated on ${maxDevicesAllowed} device(s). Contact support to reset.`);
      }
      const updatedDevices = alreadyOnThisDevice ? devices : [...devices, installId];
      tx.update(licRef, {
        status: 'active',
        activatedAt: new Date().toISOString(),
        devices: updatedDevices,
        activationCount: updatedDevices.length,
      });
      return { valid: true, plan: d.plan as PlanType, expiresAt: d.expiresAt || null, customer: d.customer, maxCompanies: d.maxCompanies || 1 };
    });
  } catch (e: any) {
    return { success: false, reason: e?.message || 'Server se connect nahi ho paya.' };
  }

  if (!txResult) return { success: false, reason: 'Activation failed. Try again.' };

  const newLicense: LicenseInfo = {
    type: 'full', plan: txResult.plan,
    activatedAt: new Date().toISOString(),
    expiresAt: txResult.expiresAt || null,
    key: trimmed, customer: txResult.customer,
    deviceId: installId,
    maxCompanies: txResult.maxCompanies,
    activationMethod: 'key',
  };
  saveStoredLicense(newLicense);
  await saveInstallation(newLicense);
  return { success: true };
};

export const activateDemo = async (): Promise<LicenseInfo> => {
  const installId = await getInstallId();
  const now       = new Date().toISOString();
  const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
  const demoLicense: LicenseInfo = { type: 'demo', plan: 'pro', activatedAt: now, expiresAt, key: null, deviceId: installId };
  saveStoredLicense(demoLicense);
  try {
    const ref = await installRef();
    await setDoc(ref, { licenseKey: null, plan: 'pro', type: 'demo', activatedAt: now, expiresAt, customer: null, updatedAt: now }, { merge: true });
  } catch { /* offline */ }
  return demoLicense;
};

export const watchLicenseRevocation = (key: string, onRevoke: () => void): (() => void) => {
  return onSnapshot(doc(db, 'licenses', key), (snap) => {
    if (!snap.exists()) { onRevoke(); return; }
    const d = snap.data();
    if (d.status === 'revoked' || (d.expiresAt && new Date(d.expiresAt).getTime() < Date.now())) {
      onRevoke();
    }
  });
};

export const validateLicense = async (): Promise<{ isValid: boolean; info: LicenseInfo | null; reason?: string }> => {
  const info = await getStoredLicenseAsync();
  if (!info) return { isValid: false, info: null, reason: 'No license found.' };
  if (!isLicenseValid(info)) return { isValid: false, info, reason: 'License expired or invalid.' };
  return { isValid: true, info };
};

export const generateTransferCode = async (): Promise<{ success: boolean; code?: string; reason?: string }> => {
  const license = getStoredLicense();
  if (!license?.key) return { success: false, reason: 'No active license to transfer.' };
  const online = await checkOnline();
  if (!online) return { success: false, reason: 'Internet required for license transfer.' };
  const code = String(Math.floor(100000 + Math.random() * 900000));
  const installId = await getInstallId();
  try {
    await setDoc(doc(db, 'transfers', license.key), {
      code, fromInstallId: installId,
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      used: false,
    });
    await runTransaction(db, async (tx) => {
      const licRef = doc(db, 'licenses', license.key!);
      const snap = await tx.get(licRef);
      if (!snap.exists()) return;
      const d = snap.data();
      const devices = Array.isArray(d.devices) ? d.devices : [];
      const updatedDevices = devices.filter((id: string) => id !== installId);
      tx.update(licRef, { status: 'active', activatedAt: d.activatedAt || new Date().toISOString(), devices: updatedDevices, activationCount: updatedDevices.length });
    });
    const ref = await installRef();
    await setDoc(ref, { licenseKey: null, deactivatedAt: new Date().toISOString() }, { merge: true });
    clearStoredLicense();
    try { await tauriInvoke('clear_license_cache'); } catch { /* not Tauri */ }
    return { success: true, code };
  } catch (e: any) {
    return { success: false, reason: e.message || 'Transfer failed. Try again.' };
  }
};

export const activateWithTransferCode = async (key: string, code: string): Promise<{ success: boolean; reason?: string }> => {
  const trimmed = key.trim().toUpperCase();
  const codeTrimmed = code.trim();
  if (!codeTrimmed || codeTrimmed.length !== 6) return { success: false, reason: 'Invalid transfer code. Must be 6 digits.' };
  try {
    const transferSnap = await getDoc(doc(db, 'transfers', trimmed));
    if (!transferSnap.exists()) return { success: false, reason: 'No transfer request found for this license key.' };
    const transfer = transferSnap.data();
    if (transfer.used) return { success: false, reason: 'This transfer code has already been used.' };
    if (transfer.code !== codeTrimmed) return { success: false, reason: 'Incorrect transfer code.' };
    if (new Date(transfer.expiresAt).getTime() < Date.now()) return { success: false, reason: 'Transfer code expired. Generate a new one from the old device.' };
    await updateDoc(doc(db, 'transfers', trimmed), { used: true, usedAt: new Date().toISOString() });
    return await activateLicense(trimmed);
  } catch (e: any) {
    return { success: false, reason: e.message || 'Transfer activation failed.' };
  }
};

// Legacy compat
export const getLicenseInfo            = (): LicenseInfo | null => getStoredLicense();
export const validateLicenseKeyContext = checkKeyFormat;
